/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import { useStoreContext } from '@/contexts/StoreContext';

interface TokenDisplayProps {
  token: string;
  label?: string;
}

export const TokenDisplay: React.FC<TokenDisplayProps> = ({ token, label }) => {
  const { t } = useTranslation();
  const { antflowServerConfig } = useStoreContext();
  const [copied, setCopied] = useState(false);
  const [snippetCopied, setSnippetCopied] = useState(false);

  const apiUrl = antflowServerConfig?.apiUrl || '<api-url>';
  const baseSnippet =
    antflowServerConfig?.launcherSnippet ??
    `antflow-launcher config --api-url  <api-url> --token <token>\nantflow-launcher run`;

  const copyText = async (text: string, onCopied: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onCopied(true);
      setTimeout(() => onCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const runSnippet = baseSnippet.replace('<api-url>', apiUrl).replace('<token>', token);
  return (
    <div className="flex flex-column gap-2">
      {label && <label className="font-medium text-sm">{label}</label>}
      <div className="p-inputgroup">
        <InputText
          value={token}
          readOnly
          className="font-mono text-sm"
          style={{ backgroundColor: 'var(--surface-ground)' }}
        />
        <Button
          icon={copied ? <Check size={16} /> : <Copy size={16} />}
          severity={copied ? 'success' : 'secondary'}
          onClick={() => copyText(token, setCopied)}
          tooltip={copied ? t(['notifs.texts.copied', 'Copied']) : t(['actions.copy', 'Copy'])}
          tooltipOptions={{ position: 'top' }}
        />
      </div>
      <small className="text-orange-500">
        <i className="pi pi-exclamation-triangle mr-1" />
        {t(['launchers.token_warning', 'Save this token now. It will not be shown again.'])}
      </small>

      <div className="flex flex-column gap-2 mt-2">
        <label className="font-medium text-sm">
          {t(['launchers.run_guide', 'Register and start your launcher with this token:'])}
        </label>
        <div
          style={{ backgroundColor: 'var(--surface-ground)' }}
          className="relative border-round-md border-1 border-gray-300 pt-3 pr-6"
        >
          <pre
            className="font-mono text-sm p-2 m-0 border-round font-medium"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
          >
            {runSnippet.replace('<api-url>', apiUrl).replace('<token>', token)}
          </pre>
          <Button
            className="absolute"
            style={{ top: '0.5rem', right: '0.5rem' }}
            icon={snippetCopied ? <Check size={16} /> : <Copy size={16} />}
            severity={snippetCopied ? 'success' : 'secondary'}
            data-pr-className="text-xs"
            text
            onClick={() =>
              copyText(runSnippet.replace('<api-url>', apiUrl).replace('<token>', token), setSnippetCopied)
            }
            tooltip={snippetCopied ? t(['notifs.texts.copied', 'Copied']) : t(['actions.copy', 'Copy'])}
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      </div>
    </div>
  );
};
